import {
  BRAND_FILTER_QUERY_KEY,
  REGION_FILTER_QUERY_KEY,
} from "staticConfiguration";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box, { type BoxProps } from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { QueryParamMultiSelect } from "core/components/QueryComponents/QueryParamMultiselect";
import type { SelectOption } from "core/entities";
import { useBrandRegionOptions } from "core/hooks/useBrandRegionOptions";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import {
  useNewsEventCategories,
  // useNewsEventsTags,
  useNewsEventsTypes,
} from "features/NewsEvents/hooks";
import { useTranslation } from "react-i18next";

export interface NewsArticlesFiltersProps {
  sx: BoxProps["sx"];
}

export function NewsArticlesFilters({ sx }: NewsArticlesFiltersProps) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:NewsFilters",
  });
  const {
    brandOptions,
    regionOptions,
    isLoading: isBrandRegionOptionsLoading,
  } = useBrandRegionOptions();

  const { isMobile } = useBreakpoints();
  const { isLoading, isError, data } = useNewsEventCategories();

  const areCategoriesReady = !isLoading && !isError && data;
  const categoryOptions =
    areCategoriesReady && Array.isArray(data?.items)
      ? [
          ...data.items
            .map(({ id, title }) => ({ label: title, value: id }))
            .filter(
              (entry): entry is { label: string; value: string } =>
                typeof entry.label === "string" &&
                typeof entry.value === "string"
            ),
          { label: t("uncategorized"), value: "uncategorized" },
        ]
      : [];

  // !TODO: re-enable when tags filtering is supported in the backend
  // const {
  //   isLoading: tagsLoading,
  //   isError: tagsError,
  //   data: tagsData,
  // } = useNewsEventsTags("news");

  const { data: typesData, isLoading: isTypesLoading } =
    useNewsEventsTypes("newstype");

  const typeOptions: SelectOption[] =
    typesData?.items
      ?.map(({ id, title }) => ({ label: title, value: id }))
      .filter(
        (item): item is { label: string; value: string } =>
          typeof item.label === "string" && typeof item.value === "string"
      ) || [];

  // const areTagsReady = !tagsLoading && !tagsError && tagsData;
  // const tagOptions = areTagsReady
  //   ? (tagsData.items?.map((tagString) => ({
  //       label: tagString,
  //       value: tagString,
  //     })) ?? [])
  //   : [];

  const BrandRegionMultiSelects = (
    <>
      <QueryParamMultiSelect
        queryKey={BRAND_FILTER_QUERY_KEY}
        options={brandOptions}
        loading={isBrandRegionOptionsLoading}
        label={t("brands")}
      />
      <QueryParamMultiSelect
        queryKey={REGION_FILTER_QUERY_KEY}
        options={regionOptions}
        loading={isBrandRegionOptionsLoading}
        label={t("regions")}
      />
    </>
  );

  const CategoryTagsTypesMultiSelects = (
    <>
      <QueryParamMultiSelect
        queryKey="categories"
        options={categoryOptions}
        loading={!areCategoriesReady}
        label={t("categories")}
      />
      {/* <QueryParamMultiSelect
        queryKey="tags"
        options={tagOptions}
        loading={!areTagsReady}
        label={t("tags")}
      /> */}
      <QueryParamMultiSelect
        queryKey="types"
        options={typeOptions}
        loading={isTypesLoading || !typesData}
        label={t("types")}
      />
    </>
  );

  if (isMobile) {
    return (
      <Box mb={4}>
        <Accordion
          disableGutters
          sx={{
            border: (theme) => `1px solid ${theme.palette.grey[400]}`,
            backgroundColor: "background.default",
            color: "text.secondary",
          }}
          elevation={0}
        >
          <AccordionSummary
            sx={{ alignItems: "center" }}
            expandIcon={<ExpandMoreIcon />}
          >
            <FilterListIcon color="inherit" />{" "}
            <Typography sx={{ mx: 2 }}>{t("filters")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              {BrandRegionMultiSelects}
              {CategoryTagsTypesMultiSelects}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        marginX: "auto",
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        "& > *": { flex: 1, minWidth: "250px" },
        ...sx,
      }}
    >
      {BrandRegionMultiSelects}
      {CategoryTagsTypesMultiSelects}
    </Box>
  );
}
